import { Injectable, signal, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { API } from './api/api';

@Injectable({
  providedIn: 'root',
})
export class UserClientSettingService {
  private _isInitialized = false;
  userClientSettings: WritableSignal<Record<string, string> | null> = signal<Record<string, string> | null>(null);

  constructor(private readonly api: API) {}

  updateUserClientSettings(data: Record<string, string> | null) {
    if (data) {
      this.userClientSettings.set({ ...data });
    } else {
      this.userClientSettings.set(null);
    }
  }

  async getUserClientSettingsFromServer(): Promise<void> {
    const userSettings = await firstValueFrom(this.api.shared_User_retrieveClientSettings().pipe(take(1)));
    this._isInitialized = true;
    this.updateUserClientSettings(userSettings);
  }

  async setUserClientSetting(key: string, value: string): Promise<void> {
    if (key == null || value == null) {
      throw new Error('Missing Key/Value pair definition');
    }
    try {
      const settings = this.userClientSettings() ?? {};
      settings[key] = value;
      const updatedSettings: Record<string, any> = await firstValueFrom(
        this.api.shared_User_updateClientSettings(settings).pipe(
          take(1),
          catchError(err => {
            throw new Error(err);
          })
        )
      );
      this.updateUserClientSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating user setting:', error);
      throw error;
    }
  }

  async clearUserClientSetting(key: string): Promise<void> {
    try {
      const settings = this.userClientSettings() ?? {};
      if (settings[key]) {
        delete settings[key];
      }
      const updatedSettings: Record<string, any> = await firstValueFrom(
        this.api.shared_User_updateClientSettings(settings).pipe(
          take(1),
          catchError(err => {
            throw new Error(err);
          })
        )
      );
      this.updateUserClientSettings(updatedSettings);
    } catch (error) {
      console.error('Error clearing user setting:', error);
      throw error;
    }
  }

  async getAllUserClientSettings(): Promise<Record<string, string>> {
    return Promise.resolve(this.userClientSettings());
  }

  async getUserClientSetting(key: string): Promise<string | null> {
    if (!this._isInitialized) {
      await this.getUserClientSettingsFromServer();
    }
    return Promise.resolve(this.userClientSettings()?.[key] ?? null);
  }

  async clearAllUserClientSettings(): Promise<void> {
    const updatedSettings: Record<string, string> = await firstValueFrom(
      this.api.shared_User_clearClientSettings().pipe(
        take(1),
        catchError(err => {
          throw new Error(err);
        })
      )
    );
    this.updateUserClientSettings(updatedSettings);
  }
}
